/**
 * Left pane of a pe3d base: its variants in folders.
 *
 * Folders are the base's versionHierarchy document, written through the pe3d
 * tree routes. A base is a shelf, so a list can hold variants of several games;
 * each row says which game and release it is. A variant missing from the tree
 * (created before the tree was saved, say) is shown at the root.
 *
 * Rows are dragged onto a folder to go into it, onto a row inside a folder to
 * join that row's folder, or onto the list's empty space to go back to the
 * top level (plain HTML drag and drop; the server refuses a folder dropped
 * into itself). The menu's "Move to…" does the same without a mouse.
 */

import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import DescriptionIcon from "@material-ui/icons/Description";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import GetAppIcon from "@material-ui/icons/GetApp";
import { BAR_BG, CONTROL_BG, DARK_SCROLLBAR_CSS, FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import { DIALOG_PAPER_PROPS, MENU_PAPER_PROPS, MUTED, ROW_HOVER, ROW_SELECTED, ROW_SELECTED_HOVER } from "./pe3dPanelKit";
import { ROOT, buildVariantTree, canDropInto, findFolder, folderTrail, treeIndex, variantIdsUnder } from "../../modules/pe3dVariantTree";
import DialogPe3dNewVariant from "./dialogPe3dNewVariant";
import DialogPe3dExport from "./dialogPe3dExport";


// The snapshot list's grid and highlights (snapshotEditor/containerSnapshotList.js),
// so the lists on either side of a preview behave the same under the pointer.
const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 72px 34px",
  columnGap: "6px",
  alignItems: "center",
  paddingLeft: 6,
  paddingRight: 6,
};

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", flex: 1, height: "100%", minWidth: 0, minHeight: 0, overflow: "hidden", backgroundColor: PANEL_BG },
  header: { display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", backgroundColor: BAR_BG, borderBottom: `1px solid ${SECTION_DIVIDER}` },
  filter: { flex: 1 },
  filterInput: { color: "#eee", fontSize: 13, backgroundColor: CONTROL_BG, borderRadius: 4, padding: "2px 6px" },
  list: { flex: 1, overflowY: "auto", overflowX: "hidden", padding: "4px 4px 8px 4px" },
  pathBar: { display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", padding: "3px 8px", backgroundColor: BAR_BG, borderTop: `1px solid ${SECTION_DIVIDER}`, fontSize: 11, color: MUTED, flexShrink: 0 },
  crumb: { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", padding: "1px 4px", borderRadius: 3, "&:hover": { backgroundColor: CONTROL_BG, color: "#eee" } },
  crumbHere: { color: "#eee" },
  crumbArrow: { fontSize: 13, flexShrink: 0, opacity: 0.6 },
  columnHeader: {
    ...ROW_GRID,
    boxSizing: "border-box",
    paddingTop: 4,
    paddingBottom: 4,
    marginBottom: 2,
    borderBottom: `1px solid ${SECTION_DIVIDER}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: PANEL_BG,
    fontSize: 12,
    color: FIELD_LABEL,
  },
  row: {
    ...ROW_GRID,
    boxSizing: "border-box",
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: 4,
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { backgroundColor: ROW_HOVER },
    "&:hover .menu": { opacity: 1 },
  },
  rowSelected: { backgroundColor: ROW_SELECTED, "&:hover": { backgroundColor: ROW_SELECTED_HOVER } },
  dropTarget: { boxShadow: "inset 0 0 0 1px #4eabb1", backgroundColor: "rgba(78, 171, 177, 0.12)" },
  listDropTarget: { boxShadow: "inset 0 0 0 1px #4eabb1" },
  dragging: { opacity: 0.45 },
  rowMain: { minWidth: 0, display: "flex", alignItems: "center", gap: 6 },
  label: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: "#eee" },
  meta: { fontSize: 11, color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 1, minWidth: 0 },
  updated: { fontSize: 11, color: MUTED, whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  menuSlot: { width: 34, height: 28, display: "flex", alignItems: "center", justifyContent: "center", "& .menu": { opacity: 0.6 } },
  icon: { fontSize: 16, color: "#8a8a8a", flexShrink: 0 },
  empty: { padding: "16px 10px", color: MUTED, fontSize: 13 },
});

/** Short date for the Updated column, as the snapshot list writes it. */
function formatUpdated(date) {
  return date ? moment(date).format("DD.MM.YY") : "";
}

function allFolders(nodes, depth = 0, out = []) {
  for (const node of nodes) {
    if (node.type !== "folder") continue;
    out.push({ id: node.id, title: `${"  ".repeat(depth)}${node.title}` });
    allFolders(node.children, depth + 1, out);
  }
  return out;
}

export default function ContainerPe3dVariantList({ editor, selectedBase, selectedGame, createSnackbar, userPermissions, account, canGrantGames }) {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [menu, setMenu] = useState(null); // { anchor, node }
  const [prompt, setPrompt] = useState(null); // { title, label, value, onSubmit }
  const [moving, setMoving] = useState(null); // node
  const [isNewVariantOpen, setIsNewVariantOpen] = useState(false);
  const [newVariantFolder, setNewVariantFolder] = useState(ROOT);
  const [exportRequest, setExportRequest] = useState(null); // { ids, folderName }: what the export dialog opens with
  const [dragged, setDragged] = useState(null); // { type, id }
  const [dropTarget, setDropTarget] = useState(null); // folder id, or ROOT

  const nodes = useMemo(() => buildVariantTree(editor.tree, editor.variants, searchTerm), [editor.tree, editor.variants, searchTerm]);
  const fullTree = useMemo(() => buildVariantTree(editor.tree, editor.variants), [editor.tree, editor.variants]);
  const folderChoices = useMemo(() => allFolders(fullTree), [fullTree]);
  const index = useMemo(() => treeIndex(fullTree), [fullTree]);

  // The folder being looked at on its own, as a Finder window is. Double-click
  // a folder to step into it; the path bar at the foot steps back out. It only
  // narrows what is listed — the folders still open and shut in place, and a
  // filter searches the whole base rather than this folder, so a search is
  // never quietly missing its answer.
  const [cwd, setCwd] = useState(ROOT);
  const isSearching = Boolean(searchTerm.trim());
  const trail = useMemo(() => folderTrail(fullTree, cwd), [fullTree, cwd]);
  // A folder that was renamed away, deleted or dragged elsewhere must not strand the list.
  useEffect(() => {
    if (cwd !== ROOT && !findFolder(fullTree, cwd)) setCwd(ROOT);
  }, [fullTree, cwd]);
  const shown = useMemo(() => {
    if (isSearching || cwd === ROOT) return nodes;
    const folder = findFolder(nodes, cwd);
    return folder ? folder.children : [];
  }, [nodes, cwd, isSearching]);
  const can = (permission) => !userPermissions || userPermissions[permission] !== false;

  const canMove = can("editContent");

  const endDrag = () => {
    setDragged(null);
    setDropTarget(null);
  };
  // Handlers for anything a row can be dropped on; `target` is the folder it
  // would go into. A row that refuses still stops the event, or it would reach
  // the list and turn into a move to the top level.
  const dropHandlers = (target) => ({
    onDragOver: (event) => {
      event.stopPropagation();
      if (!canDropInto(dragged, target, index)) {
        if (dropTarget !== null) setDropTarget(null);
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (dropTarget !== target) setDropTarget(target);
    },
    onDrop: (event) => {
      event.stopPropagation();
      if (!canDropInto(dragged, target, index)) return;
      event.preventDefault();
      const node = dragged;
      endDrag();
      if (target !== ROOT) setCollapsed((c) => ({ ...c, [target]: false }));
      editor.moveNode(node.id, target);
    },
  });

  const closeMenu = () => setMenu(null);
  // `verb` is the dialog's primary button; `danger` makes it the quiet destructive kind.
  const ask = (title, label, value, onSubmit, verb = "Save", danger = false) => setPrompt({ title, label, value, onSubmit, verb, danger });

  const menuItems = (node) => {
    if (node.type === "folder") {
      return [
        can("createVersion") && ["New variant here…", () => { setNewVariantFolder(node.id); setIsNewVariantOpen(true); }],
        can("exportPlayable") && variantIdsUnder(node).length > 0 && ["Export folder…", () => setExportRequest({ ids: variantIdsUnder(node), folderName: node.title })],
        can("editContent") && ["Rename…", () => ask("Rename folder", "Name", node.title, (name) => editor.renameFolder(node.id, name), "Rename")],
        can("editContent") && ["Move to…", () => setMoving(node)],
        can("deleteContent") && ["Delete folder (keeps its variants)", () => editor.deleteFolder(node.id)],
      ].filter(Boolean);
    }
    return [
      can("editContent") && ["Rename…", () => ask("Rename variant", "Title", node.title, (title) => editor.updateMeta(node.id, { title }), "Rename")],
      can("createVersion") && ["Duplicate…", () => ask("Duplicate variant", "Title", `${node.title} copy`, (title) => editor.duplicate(node.id, title), "Duplicate")],
      can("editContent") && ["Move to…", () => setMoving(node)],
      can("deleteContent") && ["Delete…", () => ask(`Delete "${node.title}"?`, "Type the title to confirm", "", (typed) => typed === node.title ? editor.remove(node.id) : createSnackbar("The title did not match — nothing was deleted.", "warning", false), "Delete", true)],
    ].filter(Boolean);
  };

  const renderNodes = (list, depth, parent = ROOT) =>
    list.map((node) => {
      const isFolder = node.type === "folder";
      // Always open while a search is running: a folder someone collapsed by
      // hand would otherwise hide the very thing they are searching for.
      const isOpen = isFolder && (isSearching || !collapsed[node.id]);
      const isSelected = !isFolder && String(editor.selectedID) === String(node.id);
      // A folder takes what is dropped on it; a variant row passes it to its own folder.
      const target = isFolder ? node.id : parent;
      const isDragged = dragged && dragged.type === node.type && String(dragged.id) === String(node.id);
      const isDropTarget = isFolder && dropTarget !== null && String(dropTarget) === String(node.id);
      return (
        <React.Fragment key={`${node.type}-${node.id}`}>
          <div
            className={`${classes.row} ${isSelected ? classes.rowSelected : ""} ${isDropTarget ? classes.dropTarget : ""} ${isDragged ? classes.dragging : ""}`}
            draggable={canMove}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = "move";
              // Firefox starts no drag without data.
              event.dataTransfer.setData("text/plain", node.title);
              setDragged({ type: node.type, id: node.id });
            }}
            onDragEnd={endDrag}
            {...(dragged ? dropHandlers(target) : {})}
            onClick={() => (isFolder ? setCollapsed((c) => ({ ...c, [node.id]: !c[node.id] })) : editor.select(node.id))}
            onDoubleClick={() => isFolder && !isSearching && setCwd(node.id)}
          >
            <div className={classes.rowMain} style={{ paddingLeft: depth * 14 }}>
              {isFolder ? (isOpen ? <FolderOpenIcon className={classes.icon} /> : <FolderIcon className={classes.icon} />) : <DescriptionIcon className={classes.icon} />}
              <span className={classes.label} title={node.title}>
                {node.title}
              </span>
              {!isFolder && (
                <span className={classes.meta}>
                  {String(node.variant.game || "").replace("@pe3d-games/", "")}@{node.variant.release}
                </span>
              )}
              {isFolder && <span className={classes.meta}>{variantIdsUnder(node).length}</span>}
            </div>
            <span className={classes.updated}>{isFolder ? "" : formatUpdated(node.variant.dateUpdated || node.variant.dateCreated)}</span>
            <div className={classes.menuSlot}>
              <IconButton
                className="menu"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenu({ anchor: event.currentTarget, node });
                }}
              >
                <MoreVertIcon style={{ fontSize: 14 }} />
              </IconButton>
            </div>
          </div>
          {isOpen && renderNodes(node.children, depth + 1, node.id)}
        </React.Fragment>
      );
    });

  return (
    <div className={classes.root}>
      <style>{DARK_SCROLLBAR_CSS}</style>
      <div className={classes.header}>
        <TextField
          className={classes.filter}
          placeholder="Filter variants"
          value={searchTerm}
          size="small"
          InputProps={{ className: classes.filterInput }}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {can("createVersion") && (
          <Tooltip title="New variant">
            <IconButton size="small" onClick={() => { setNewVariantFolder(ROOT); setIsNewVariantOpen(true); }}>
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {can("createVersion") && (
          <Tooltip title="New folder">
            <IconButton size="small" onClick={() => ask("New folder", "Name", "", (name) => editor.createFolder(name, ROOT), "Create")}>
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* Any number of variants in one go; opens on the one being edited. */}
        {can("exportPlayable") && (
          <Tooltip title="Export variants">
            <span>
              <IconButton
                size="small"
                disabled={editor.variants.length === 0}
                onClick={() => setExportRequest({ ids: editor.selectedID ? [String(editor.selectedID)] : [], folderName: "" })}
              >
                <GetAppIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Reload the variants">
          <span>
            <IconButton size="small" onClick={editor.refresh} disabled={editor.isLoading}>
              {editor.isLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <div
        className={`${classes.list} proto-dark-scroll ${dragged && String(dropTarget) === String(ROOT) ? classes.listDropTarget : ""}`}
        {...(dragged ? dropHandlers(ROOT) : {})}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setDropTarget(null);
        }}
      >
        <div className={classes.columnHeader}>
          <span>Variants ({editor.variants.length})</span>
          <span style={{ textAlign: "right" }}>Updated</span>
          <span />
        </div>
        {editor.isLoading && editor.variants.length === 0 && <div className={classes.empty}>Loading variants…</div>}
        {!editor.isLoading && editor.variants.length === 0 && (
          <div className={classes.empty}>No variants yet — start one, or all of a game&apos;s own at once, with the New variant button above.</div>
        )}
        {editor.variants.length > 0 && searchTerm.trim() && nodes.length === 0 && <div className={classes.empty}>No variant matches this filter.</div>}
        {/* The way out, at the top of the list where the eye already is: the
            path bar at the foot is the other one, and a tall pane can push it
            out of sight. */}
        {!isSearching && cwd !== ROOT && (
          <div
            className={classes.row}
            {...(dragged ? dropHandlers(index.parentOf[`folder-${cwd}`] ?? ROOT) : {})}
            onClick={() => setCwd(index.parentOf[`folder-${cwd}`] ?? ROOT)}
          >
            <div className={classes.rowMain}>
              <ArrowUpwardIcon className={classes.icon} />
              <span className={classes.label}>..</span>
            </div>
            <span className={classes.updated} />
            <div className={classes.menuSlot} />
          </div>
        )}
        {!isSearching && cwd !== ROOT && shown.length === 0 && <div className={classes.empty}>This folder is empty.</div>}
        {renderNodes(shown, 0, isSearching ? ROOT : cwd)}
      </div>

      {/* The path bar, as the foot of a Finder window: where the list is, and
          the way back out. Hidden while filtering, which searches everywhere. */}
      {!isSearching && (
        <div className={classes.pathBar}>
          <span
            className={`${classes.crumb} ${cwd === ROOT ? classes.crumbHere : ""}`}
            onClick={() => setCwd(ROOT)}
          >
            Top level
          </span>
          {trail.map((folder, position) => (
            <React.Fragment key={folder.id}>
              <ChevronRightIcon className={classes.crumbArrow} />
              <span
                className={`${classes.crumb} ${position === trail.length - 1 ? classes.crumbHere : ""}`}
                title={folder.title}
                onClick={() => setCwd(folder.id)}
              >
                {folder.title}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      <Menu anchorEl={menu && menu.anchor} open={Boolean(menu)} onClose={closeMenu} PaperProps={MENU_PAPER_PROPS}>
        {menu &&
          menuItems(menu.node).map(([label, action]) => (
            <MenuItem key={label} dense onClick={() => { closeMenu(); action(); }}>
              {label}
            </MenuItem>
          ))}
      </Menu>

      <Dialog open={Boolean(prompt)} onClose={() => setPrompt(null)} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER_PROPS}>
        {prompt && (
          <>
            <DialogTitle>{prompt.title}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label={prompt.label}
                value={prompt.value}
                inputProps={{ maxLength: 120 }}
                onChange={(event) => setPrompt({ ...prompt, value: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && prompt.value.trim()) {
                    prompt.onSubmit(prompt.value.trim());
                    setPrompt(null);
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrompt(null)}>Cancel</Button>
              <Button
                color={prompt.danger ? "secondary" : "primary"}
                variant={prompt.danger ? "text" : "contained"}
                disabled={!prompt.value.trim()}
                onClick={() => {
                  prompt.onSubmit(prompt.value.trim());
                  setPrompt(null);
                }}
              >
                {prompt.verb}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={Boolean(moving)} onClose={() => setMoving(null)} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER_PROPS}>
        <DialogTitle>Move “{moving && moving.title}” to</DialogTitle>
        <DialogContent>
          {[{ id: ROOT, title: "Top level" }, ...folderChoices]
            .filter((folder) => canDropInto(moving, folder.id, index))
            .map((folder) => (
              <MenuItem
                key={folder.id}
                dense
                onClick={async () => {
                  const node = moving;
                  setMoving(null);
                  await editor.moveNode(node.id, folder.id);
                }}
                style={{ whiteSpace: "pre" }}
              >
                {folder.title}
              </MenuItem>
            ))}
          <div style={{ fontSize: 12, color: FIELD_LABEL, opacity: 0.8, marginTop: 10 }}>Or drag it in the list onto a folder.</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoving(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <DialogPe3dNewVariant
        isOpen={isNewVariantOpen}
        editor={editor}
        folderID={newVariantFolder}
        folderTitle={((folderChoices.find((folder) => String(folder.id) === String(newVariantFolder)) || {}).title || "").trim()}
        canGrantGames={canGrantGames}
        onClose={() => setIsNewVariantOpen(false)}
        createSnackbar={createSnackbar}
      />

      <DialogPe3dExport
        isOpen={Boolean(exportRequest)}
        onClose={() => setExportRequest(null)}
        editor={editor}
        selectedBase={selectedBase}
        selectedGame={selectedGame}
        createSnackbar={createSnackbar}
        account={account}
        initialIDs={exportRequest ? exportRequest.ids : []}
        folderName={exportRequest ? exportRequest.folderName : ""}
      />
    </div>
  );
}
